import { makeAnalyticsSourceID, SCREEN_EVENT_TYPE } from '@atlassian/mpac-analytics';
import { Set } from 'immutable';
// Analytics sources
// To add a new screen, you MUST:
// 1) Add a const below representing the screen's name and description
// 2) Add the const from step 1 to allAnalyticsSources
//
// If you skip any step, you will get an error at runtime, because the screen will not be registered
// in Data Portal during the deployment process.

// Names MUST all begin with "embeddedMarketplace", and end with their type:
// "Screen", "Modal" or "Drawer".

export const ANALYTICS_SCREEN_DISCOVER = {
  name: makeAnalyticsSourceID('embeddedMarketplaceDiscoverScreen'),
  details: "Front-page 'Marketplace apps' view"
};
export const ANALYTICS_SCREEN_SEARCH = {
  name: makeAnalyticsSourceID('embeddedMarketplaceSearchScreen'),
  details: 'Search results'
};
export const ANALYTICS_SCREEN_MANAGE = {
  name: makeAnalyticsSourceID('embeddedMarketplaceManageScreen'),
  details: "'Manage apps' view"
};
export const ANALYTICS_SCREEN_MANAGE_APP = {
  name: makeAnalyticsSourceID('embeddedMarketplaceManageAppScreen'),
  details: 'Manage permissions etc. for a single app'
};
export const ANALYTICS_SCREEN_APP_LISTING = {
  /* the analytics source value of `embeddedMarketplaceAppListingScreen` is used in vendor-service's Segment integration with partners.
  Any change to this value should be accompanied by a corresponding update in vendor-service.*/
  name: makeAnalyticsSourceID('embeddedMarketplaceAppListingScreen'),
  details: 'Details for a single app on Marketplace'
};
export const ANALYTICS_SCREEN_MANAGE_APP_REQUESTS = {
  name: makeAnalyticsSourceID('embeddedMarketplaceMangeAppRequestsScreen'),
  details: 'A dashboard for the admins to manage the App requests'
};
export const ANALYTICS_SCREEN_MANAGE_PROMOTIONS = {
  name: makeAnalyticsSourceID('embeddedMarketplaceManagePromotionsScreen'),
  details: 'A dashboard for the admins to manage the Promotions'
};
export const allAnalyticsSourceScreens = Set([ANALYTICS_SCREEN_DISCOVER, ANALYTICS_SCREEN_SEARCH, ANALYTICS_SCREEN_MANAGE, ANALYTICS_SCREEN_MANAGE_APP, ANALYTICS_SCREEN_APP_LISTING, ANALYTICS_SCREEN_MANAGE_APP_REQUESTS, ANALYTICS_SCREEN_MANAGE_PROMOTIONS]);
export const ANALYTICS_MODAL_DETERMINING = {
  name: makeAnalyticsSourceID('embeddedMarketplaceInstallDeterminingModal'),
  details: 'Dialog displayed while determining which install dialog to use'
};
export const ANALYTICS_MODAL_INSTALL = {
  name: makeAnalyticsSourceID('embeddedMarketplaceInstallModal'),
  details: 'Install dialog for Two Legged apps and deep-linked installs'
};
export const ANALYTICS_MODAL_NO_INSTALL_PERMISSION = {
  name: makeAnalyticsSourceID('embeddedMarketplaceNoInstallPermissionModal'),
  details: 'Install dialog for Two Legged apps and deep-linked installs but the user does not have install permissions'
};
export const ANALYTICS_MODAL_CUSTOM_INSTALL = {
  name: makeAnalyticsSourceID('embeddedMarketplaceInstallCustomModal'),
  details: "Custom app dialog with the 'Installation URL' tab selected"
};
export const ANALYTICS_MODAL_CREATE_TOKEN = {
  name: makeAnalyticsSourceID('embeddedMarketplaceCreateTokenModal'),
  details: "Custom app dialog with the 'API tokens' tab selected"
};
export const ANALYTICS_MODAL_INSTRUCTIONAL = {
  name: makeAnalyticsSourceID('embeddedMarketplaceInstallInstructionalModal'),
  details: 'Install dialog for Marketplace listings that are just instructions to follow'
};
export const ANALYTICS_MODAL_UPGRADE = {
  name: makeAnalyticsSourceID('embeddedMarketplaceUpgradeModal'),
  details: 'Upgrade dialog for Two Legged apps'
};
export const ANALYTICS_MODAL_UNINSTALL_SITE = {
  name: makeAnalyticsSourceID('embeddedMarketplaceUninstallFromSiteConfirmModal'),
  details: 'Dialog confirming if you want to uninstall an app from the whole site'
};
export const ANALYTICS_MODAL_REQUEST_APP = {
  name: makeAnalyticsSourceID('embeddedMarketplaceRequestAppModal'),
  details: 'Request app dialog when the user clicks on request app button'
};
export const ANALYTICS_MODAL_APPLY_PROMOTIONS = {
  name: makeAnalyticsSourceID('embeddedMarketplaceApplyPromotionModal'),
  details: 'Apply promotions dialog when the user clicks on apply promotions button'
};
export const ANALYTICS_MODAL_SHOW_TOKEN = {
  name: makeAnalyticsSourceID('embeddedMarketplaceShowTokenModal'),
  details: 'Dialog displaying a token and how to use it'
};
export const ANALYTICS_MODAL_APP_REQUEST_COMMENTS_MODAL = {
  name: makeAnalyticsSourceID('embeddedMarketplaceAppRequestCommentsModal'),
  details: 'Dialog displaying the request comments from end users to the admin'
};
export const ANALYTICS_MODAL_REJECT_REQUEST = {
  name: makeAnalyticsSourceID('embeddedMarketplaceRejectRequestModal'),
  details: 'Modal when admin replies and rejects requests for an app'
};
export const allAnalyticsSourceModals = Set([ANALYTICS_MODAL_DETERMINING, ANALYTICS_MODAL_NO_INSTALL_PERMISSION, ANALYTICS_MODAL_INSTALL, ANALYTICS_MODAL_CUSTOM_INSTALL, ANALYTICS_MODAL_CREATE_TOKEN, ANALYTICS_MODAL_INSTRUCTIONAL, ANALYTICS_MODAL_UPGRADE, ANALYTICS_MODAL_UNINSTALL_SITE, ANALYTICS_MODAL_SHOW_TOKEN, ANALYTICS_MODAL_REQUEST_APP, ANALYTICS_MODAL_APP_REQUEST_COMMENTS_MODAL, ANALYTICS_MODAL_REJECT_REQUEST, ANALYTICS_MODAL_APPLY_PROMOTIONS]);

/* Start: Standalone Sources */
/* These source modal events are fired manually outside of the ScreenView redux middleware */
export const ANALYTICS_MODAL_STANDALONE_INSTALL_DETERMINING = {
  name: makeAnalyticsSourceID('marketplaceInstallDeterminingModal'),
  details: 'Dialog displayed while determining which install dialog to use',
  type: SCREEN_EVENT_TYPE
};
export const ANALYTICS_MODAL_STANDALONE_INSTALL = {
  /* the analytics source value of `marketplaceInstallModal` is used in vendor-service's Segment integration with partners.
  Any change to this value should be accompanied by a corresponding update in vendor-service.*/
  name: makeAnalyticsSourceID('marketplaceInstallModal'),
  details: 'Dialog displayed for installable apps with scopes information and other app details',
  type: SCREEN_EVENT_TYPE
};
export const ANALYTICS_MODAL_STANDALONE_INSTRUCTIONAL_APP_INSTALL = {
  name: makeAnalyticsSourceID('marketplaceInstallInstructionalModal'),
  details: 'Dialog displayed for instructional apps that are just instructions to follow',
  type: SCREEN_EVENT_TYPE
};
/* End: Standalone Install Dialog Sources */

export const ANALYTICS_MODAL_STANDALONE_APP_REQUEST = {
  name: makeAnalyticsSourceID('marketplaceAppRequestModal'),
  details: 'Dialog displayed for requestable apps with app details',
  type: SCREEN_EVENT_TYPE
};
export const allStandaloneAnalyticsSources = Set([ANALYTICS_MODAL_STANDALONE_INSTALL_DETERMINING, ANALYTICS_MODAL_STANDALONE_INSTALL, ANALYTICS_MODAL_STANDALONE_INSTRUCTIONAL_APP_INSTALL, ANALYTICS_MODAL_STANDALONE_APP_REQUEST]);
export const allAnalyticsSources = allAnalyticsSourceScreens.concat(allAnalyticsSourceModals).concat(allStandaloneAnalyticsSources);